'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SensorsIcon from '@mui/icons-material/Sensors';
import { Box, Button, IconButton, Typography } from '@mui/material';

import { LiveBadge } from '@/components';
import { useT } from '@/hooks';

import { ROUTES } from '@/constants';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';

interface GameCardHeaderProps {
  gameId: string;
  isLive: boolean;
  ended?: boolean;
  period?: string;
  elapsed?: string;
  sportSlug: string;
  gameViewCount: number;
  time: string;
  volume: string;
  isPreviousMarket?: boolean;
  resolutionSource?: string;
  leagueName?: string;
}

export const GameCardHeader = ({
  gameId,
  isLive,
  ended,
  period,
  elapsed,
  sportSlug,
  gameViewCount,
  time,
  volume,
  isPreviousMarket = false,
  resolutionSource,
  leagueName,
}: GameCardHeaderProps) => {
  const router = useRouter();
  const { t } = useT();

  const handleGameViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (gameId) {
      router.push(ROUTES.GAME_DETAIL(leagueName || sportSlug || 'live', gameId));
    }
  };

  // If the game has ended, the 'period' field (from WebSocket) or 'status' (from API)
  // contains the terminal state like "Final" or "finished".
  const displayTimeText = time || period || '--:--';
  const showLiveBadge = isLive && !ended;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}
      >
        <Box
          sx={{
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: showLiveBadge ? { xs: 0.75, sm: 1.5 } : { xs: 0.5, sm: 1 },
            flexShrink: 0,
          }}
        >
          {showLiveBadge && <LiveBadge dotSize={6} fontSize="0.75rem" />}
          {isLive && period && (
            <Typography
              noWrap
              sx={{
                fontFamily: MONO_FONT,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              {period}
            </Typography>
          )}
          {isLive && elapsed && (
            <Typography
              noWrap
              sx={{
                fontFamily: MONO_FONT,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              {elapsed}
            </Typography>
          )}
          <Box
            sx={{
              fontFamily: MONO_FONT,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              fontWeight: 700,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isPreviousMarket ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 2 },
                  minWidth: 0,
                  flex: 1,
                  bgcolor: 'action.selected',
                  borderRadius: 1,
                  px: { xs: 1, sm: 2 },
                  py: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  {t('sports.final', { defaultValue: 'Final' })}
                </Typography>
              </Box>
            ) : (
              displayTimeText
            )}
          </Box>
        </Box>

        {volume && (
          <Typography
            noWrap
            sx={{
              fontFamily: MONO_FONT,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: 'text.secondary',
              fontWeight: 500,
              ml: { xs: 0.5, sm: 0 },
            }}
          >
            {volume}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
        {isLive && resolutionSource && (
          <IconButton
            component="a"
            href={resolutionSource}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{
              bgcolor: 'action.selected',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              width: { xs: 28, sm: 34 },
              height: { xs: 28, sm: 34 },
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <SensorsIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
          </IconButton>
        )}

        <Button
          onClick={handleGameViewClick}
          disabled={!gameId}
          sx={{
            bgcolor: 'action.selected',
            color: 'text.primary',
            borderRadius: '20px',
            textTransform: 'none',
            fontSize: { xs: '0.7rem', sm: '0.8125rem' },
            fontWeight: 600,
            px: { xs: 1, sm: 2 },
            py: 0.5,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-disabled': {
              opacity: 0.5,
              bgcolor: 'action.disabledBackground',
            },
          }}
          endIcon={<ChevronRightIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
        >
          {gameViewCount > 0 && (
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
              {gameViewCount}
            </Box>
          )}
          {t('sports.gameView', { defaultValue: 'Game View' })}
        </Button>
      </Box>
    </Box>
  );
};
